/**
 * Default contexts and examples for Image Lab
 * Provides starting points for common programming concepts
 */

export interface DefaultContext {
  id: string
  name: string
  languagePair: string
  codeElement: string
  surface: string
  concept: string
  paradigm: string
  suggestedDiagramType: string
}

export const defaultContexts: DefaultContext[] = [
  {
    id: 'c-rust-match',
    name: 'C → Rust: Pattern Matching (match vs switch)',
    languagePair: 'C → Rust',
    codeElement: 'match',
    surface: 'C: switch statement → Rust: match expression',
    concept: 'In this fibonacci function, Rust\'s match requires exhaustive pattern coverage (the _ catch-all arm is mandatory), while C\'s switch would require explicit break statements to avoid fall-through. Rust\'s match also returns a value directly, making it expression-based rather than statement-based.',
    paradigm: 'Rust enforces exhaustiveness and eliminates fall-through bugs by design, reflecting its philosophy of making errors impossible at compile-time rather than relying on programmer discipline like C does.',
    suggestedDiagramType: 'control flow'
  },
  {
    id: 'c-rust-ownership',
    name: 'C → Rust: Memory Management (Ownership)',
    languagePair: 'C → Rust',
    codeElement: 'String',
    surface: 'C: malloc/free → Rust: ownership and RAII',
    concept: 'Rust\'s String type automatically manages memory through ownership. When a String goes out of scope, its memory is automatically freed. In C, you must manually call free() to deallocate heap memory allocated with malloc(), and forgetting to do so causes memory leaks.',
    paradigm: 'Rust\'s ownership system prevents memory leaks and use-after-free bugs at compile time through its borrow checker. C relies on programmer discipline to manually track and free memory, making it easy to introduce bugs.',
    suggestedDiagramType: 'memory model'
  },
  {
    id: 'python-rust-typing',
    name: 'Python → Rust: Type System',
    languagePair: 'Python → Rust',
    codeElement: 'type annotations',
    surface: 'Python: optional type hints → Rust: mandatory static types',
    concept: 'Python allows duck typing and optional type hints that are not enforced at runtime. Rust requires explicit type declarations for function parameters and return values, and the compiler enforces type safety at compile time.',
    paradigm: 'Rust uses static typing to catch type errors before runtime, trading some flexibility for guaranteed safety. Python prioritizes developer flexibility with dynamic typing, catching type errors only when the code executes.',
    suggestedDiagramType: 'type system'
  },
  {
    id: 'js-rust-async',
    name: 'JavaScript → Rust: Async/Await',
    languagePair: 'JavaScript → Rust',
    codeElement: 'async/await',
    surface: 'JavaScript: Promise-based async → Rust: Future-based async',
    concept: 'Both languages use async/await syntax, but JavaScript Promises execute eagerly (start immediately), while Rust Futures are lazy (only execute when polled/awaited). JavaScript has a built-in event loop, while Rust requires you to choose and configure an async runtime like Tokio.',
    paradigm: 'JavaScript\'s async model is designed for immediate execution with automatic scheduling. Rust\'s zero-cost abstraction philosophy means Futures don\'t run until explicitly driven, giving you control over scheduling at the cost of more manual setup.',
    suggestedDiagramType: 'execution timeline'
  },
  {
    id: 'cpp-rust-vectors',
    name: 'C++ → Rust: Dynamic Arrays (Vec vs vector)',
    languagePair: 'C++ → Rust',
    codeElement: 'Vec',
    surface: 'C++: std::vector<T> → Rust: Vec<T>',
    concept: 'Both provide dynamically-sized arrays with similar operations (push, pop, indexing). Key difference: Rust\'s Vec enforces ownership rules - you cannot have mutable and immutable references simultaneously, preventing iterator invalidation bugs that C++ allows.',
    paradigm: 'C++ trusts the programmer to avoid iterator invalidation (undefined behavior). Rust prevents these bugs at compile time through the borrow checker, making common C++ mistakes impossible.',
    suggestedDiagramType: 'data structure'
  },
  {
    id: 'java-rust-null',
    name: 'Java → Rust: Null Safety (Option)',
    languagePair: 'Java → Rust',
    codeElement: 'Option<T>',
    surface: 'Java: null references → Rust: Option<T> enum',
    concept: 'Java allows any reference to be null, requiring runtime null checks. Rust uses Option<T> to explicitly represent "may or may not have a value", forcing you to handle the None case at compile time through pattern matching.',
    paradigm: 'Java inherited null from C/C++, leading to NullPointerExceptions at runtime. Rust eliminates null entirely, using algebraic data types (Option) to make the presence or absence of values explicit and checked by the compiler.',
    suggestedDiagramType: 'type system'
  },
  {
    id: 'python-js-classes',
    name: 'Python → JavaScript: Class Syntax',
    languagePair: 'Python → JavaScript',
    codeElement: 'class',
    surface: 'Python: class with explicit self → JavaScript: class with implicit this',
    concept: 'Python classes require explicit "self" as the first parameter of methods, making instance access obvious. JavaScript uses implicit "this" binding that can change depending on how a function is called, leading to common bugs with callbacks.',
    paradigm: 'Python favors explicit over implicit (Zen of Python). JavaScript evolved from prototype-based to class-based syntax while keeping its dynamic this binding, prioritizing flexibility over predictability.',
    suggestedDiagramType: 'syntax comparison'
  },
  {
    id: 'c-python-memory',
    name: 'C → Python: Memory Management',
    languagePair: 'C → Python',
    codeElement: 'memory allocation',
    surface: 'C: manual malloc/free → Python: automatic garbage collection',
    concept: 'C requires explicit memory allocation with malloc() and deallocation with free(). Python automatically manages memory through reference counting and cyclic garbage collection, freeing objects when no references remain.',
    paradigm: 'C gives you complete control over memory at the cost of manual management and potential bugs (leaks, use-after-free). Python trades some performance for programmer convenience, handling memory automatically.',
    suggestedDiagramType: 'memory model'
  }
]

export const defaultTemplates = [
  {
    name: 'Control Flow - Exhaustiveness',
    diagramType: 'control flow',
    visualStyle: 'clean',
    colorScheme: 'language-coded',
    customInstructions: 'Emphasize compile-time safety guarantees. Use visual indicators (X marks, checkmarks) to show prevented bugs.'
  },
  {
    name: 'Memory Model - Ownership',
    diagramType: 'memory model',
    visualStyle: 'detailed',
    colorScheme: 'language-coded',
    customInstructions: 'Show stack vs heap allocation. Use arrows to indicate ownership transfer and borrowing. Include drop/destructor annotations.'
  },
  {
    name: 'Type System Comparison',
    diagramType: 'type system',
    visualStyle: 'clean',
    colorScheme: 'language-coded',
    customInstructions: 'Use side-by-side comparison. Highlight compile-time vs runtime type checking differences.'
  },
  {
    name: 'Syntax Chart - Side by Side',
    diagramType: 'syntax comparison',
    visualStyle: 'clean',
    colorScheme: 'language-coded',
    customInstructions: 'Create parallel columns showing equivalent syntax. Use consistent indentation and highlighting for corresponding elements.'
  },
  {
    name: 'Execution Timeline - Async',
    diagramType: 'execution timeline',
    visualStyle: 'detailed',
    colorScheme: 'language-coded',
    customInstructions: 'Show time progressing left to right. Use horizontal lanes for different execution contexts. Include event loop/runtime interactions.'
  }
]
